import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"
import { computeSynergies } from "../../../../../models/colyseus-models/synergies"
import type { IBot, IDetailledPokemon } from "../../../../../types/models/bot-v2"
import PokemonFactory from "../../../../../models/pokemon-factory"
import { Emotion, PkmWithCustom, Role, Transfer } from "../../../../../types"
import { Ability } from "../../../../../types/enum/Ability"
import { AbilityPerTM, Item, TMs } from "../../../../../types/enum/Item"
import { Pkm } from "../../../../../types/enum/Pokemon"
import { Synergy } from "../../../../../types/enum/Synergy"
import { isOnBench } from "../../../../../utils/board"
import { schemaValues as values } from "../../../../../utils/schemas"
import { selectSpectatedPlayer, useAppSelector } from "../../../hooks"
import { rooms } from "../../../network"
import Synergies from "../synergy/synergies"
import BotAvatar from "./bot-avatar"
import ItemPicker from "./item-picker"
import PokemonPicker from "./pokemon-picker"
import SelectedEntity from "./selected-entity"
import TeamEditor from "./team-editor"
import "./team-builder.css"

export default function TeamBuilder(props: {
  bot?: IBot
  onChangeAvatar?: (pkm: PkmWithCustom) => void
  board: IDetailledPokemon[]
  updateBoard: (board: IDetailledPokemon[]) => void
  selection?: Item | PkmWithCustom
  onSelectionUpdate?: (selection: Item | PkmWithCustom) => void
  onSelectionChange?: (selection?: Item | PkmWithCustom) => void
  emitInitialSelection?: boolean
  showSelectedEntity?: boolean
  showActions?: boolean
  showItemPicker?: boolean
  showSynergies?: boolean
  onResetBoard?: () => void
  highlightedCells?: Array<{
    x: number
    y: number
    className: string
    title?: string
  }>
  error?: string
  disabled?: boolean
  fileName?: string
}) {
  const { t } = useTranslation()
  const [internalSelection, setInternalSelection] = useState<Item | PkmWithCustom>({
    name: Pkm.MAGIKARP,
    shiny: false,
    emotion: Emotion.NORMAL
  })
  const selection = props.selection ?? internalSelection

  const ingame = useLocation().pathname === "/game"
  const inBotBuilder = useLocation().pathname.startsWith("/bot-builder")
  const spectatedPlayer = useAppSelector(selectSpectatedPlayer)
  const [board, setBoard] = useState<IDetailledPokemon[]>(props.board ?? [])
  const hasEmittedSelection = useRef(false)
  const isAdmin = useAppSelector(
    (state) => state.network.profile?.role === Role.ADMIN
  )
  const setSelectionValue = useMemo(
    () =>
      (nextSelection: Item | PkmWithCustom) => {
        props.onSelectionUpdate?.(nextSelection)
        if (props.selection === undefined) {
          setInternalSelection(nextSelection)
        }
      },
    [props.onSelectionUpdate, props.selection]
  )

  useEffect(() => {
    if (props.board) setBoard(props.board) // keep local state in sync with parent prop
  }, [props.board])

  useEffect(() => {
    if (!hasEmittedSelection.current && props.emitInitialSelection !== true) {
      hasEmittedSelection.current = true
      return
    }

    hasEmittedSelection.current = true
    if (Object.values(Pkm).includes((selection as PkmWithCustom).name)) {
      const isDetailedSelection =
        typeof selection === "object" &&
        selection != null &&
        "x" in selection &&
        "y" in selection
      const selectedDetailedPokemon =
        isDetailedSelection
          ? board.find(
              (pokemon) =>
                pokemon.x === (selection as IDetailledPokemon).x &&
                pokemon.y === (selection as IDetailledPokemon).y
            ) ?? selection
          : selection
      props.onSelectionChange?.(selectedDetailedPokemon as Item | PkmWithCustom)
      return
    }

    props.onSelectionChange?.(selection)
  }, [selection, board, props.onSelectionChange])

  function updateBoard(board) {
    if (props.updateBoard) props.updateBoard(board)
    else setBoard(board)
  }

  const [selectedPokemon, setSelectedPokemon] = useState<IDetailledPokemon>()

  const synergies: [Synergy, number][] = useMemo(() => {
    const map = computeSynergies(
      board.map((p) => {
        const pkm = PokemonFactory.createPokemonFromName(p.name, {
          emotion: p.emotion,
          shiny: p.shiny
        })
        pkm.positionX = p.x
        pkm.positionY = p.y
        p.items.forEach((item) => {
          pkm.items.add(item)
        })
        return pkm
      })
    )
    return [...map.entries()]
  }, [board])

  function addPokemon(x: number, y: number, pkm: PkmWithCustom) {
    let existingItems
    const i = board.findIndex((p) => p.x === x && p.y === y)
    if (i >= 0) {
      existingItems = board[i].items
      board.splice(i, 1)
    }
    const newPokemon: IDetailledPokemon = {
      ...pkm,
      x,
      y,
      items: existingItems ?? []
    }
    setSelectedPokemon(newPokemon)
    updateBoard([...board, newPokemon])
  }

  function addItem(x: number, y: number, item: Item) {
    const p = board.find((p) => p.x === x && p.y === y)
    if (!p) {
      return
    }

    const pokemon = buildDetailedPokemonForBuilder(p)

    if (TMs.includes(item)) {
      const ability = AbilityPerTM[item]
      if (!ability) {
        return
      }

      if (!pokemon.types.has(Synergy.HUMAN)) {
        return
      }

      p.tm = ability
      setSelectionValue({ ...p })
      setSelectedPokemon(p)
    } else if (item === Item.CELL_BATTERY) {
      if (!pokemon.types.has(Synergy.ELECTRIC)) {
        return
      }

      p.supercharged = true
      setSelectionValue({ ...p })
      setSelectedPokemon(p)
    } else if (item === Item.FIRE_SHARD) {
      if (!pokemon.types.has(Synergy.FIRE)) {
        return
      }

      p.fireShardCount = (p.fireShardCount ?? 0) + 1
      setSelectionValue({ ...p })
      setSelectedPokemon(p)
    } else if (item === Item.SMOKED_FILET) {
      p.smokedFiletCount = Math.min((p.smokedFiletCount ?? 0) + 1, 10)
      setSelectionValue({ ...p })
      setSelectedPokemon(p)
    } else if (item === Item.RARE_CANDY) {
      if (p.items.includes(Item.EVIOLITE)) {
        return
      }

      const evolvedName = getRareCandyEvolutionTarget(pokemon)
      if (!evolvedName) {
        return
      }

      p.name = evolvedName
      setSelectionValue({ ...p })
      setSelectedPokemon(p)
    } else if (p.items.length < 3) {
      p.items.push(item)
    } else {
      p.items = [item]
    }

    updateBoard([...board])
  }

  function handleEditorClick(
    x: number,
    y: number,
    rightClick: boolean,
    itemIndex?: number
  ) {
    const pokemonOnCell = board.find((p) => p.x === x && p.y === y)
    if (rightClick) {
      if (itemIndex !== undefined) {
        // remove item
        pokemonOnCell?.items.splice(itemIndex, 1)
        updateBoard([...board])
      } else {
        // remove pokemon
        updateBoard(board.filter((p) => p !== pokemonOnCell))
        if (
          selectedPokemon &&
          selectedPokemon.x === x &&
          selectedPokemon.y === y
        ) {
          setSelectedPokemon(undefined)
        }
      }
    } else if (pokemonOnCell) {
      setSelectionValue(pokemonOnCell)
      setSelectedPokemon(pokemonOnCell)
    } else if (Object.values(Pkm).includes((selection as PkmWithCustom).name)) {
      addPokemon(x, y, selection as PkmWithCustom)
    } else if (Object.keys(Item).includes(selection as Item)) {
      addItem(x, y, selection as Item)
    }
  }

  function handleDrop(x: number, y: number, e: React.DragEvent) {
    e.stopPropagation()
    e.preventDefault()
    const data = e.dataTransfer.getData("text/plain")
    if (data.startsWith("cell")) {
      const [type, originX, originY] = data.split(",").map(Number)
      const pkm = board.find((p) => p.x === originX && p.y === originY)
      const otherPokemonOnCell = board.find((p) => p.x === x && p.y === y)
      if (pkm) {
        if (otherPokemonOnCell) {
          otherPokemonOnCell.x = originX
          otherPokemonOnCell.y = originY
        }
        pkm.x = x
        pkm.y = y
        updateBoard([...board])
      }
    } else if (data.startsWith("pokemon")) {
      const [type, name] = data.split(",") as [string, Pkm]
      const pkm: PkmWithCustom = {
        name,
        emotion: Emotion.NORMAL,
        shiny: false
      }
      addPokemon(x, y, pkm)
      setSelectionValue(pkm)
    } else if (data.startsWith("item")) {
      const [type, item] = data.split(",") as [string, Item]
      addItem(x, y, item)
      setSelectionValue(item)
    }
  }

  function getFirstEmptyCell(): { x: number; y: number } | null {
    for (let y = 1; y <= 3; y++) {
      for (let x = 0; x < 8; x++) {
        if (board.find((p) => p.x === x && p.y === y) === undefined) {
          return { x, y }
        }
      }
    }
    return null
  }

  function addPokemonOnFirstEmptyCell(entity: PkmWithCustom) {
    const firstEmptyCell = getFirstEmptyCell()
    if (firstEmptyCell) {
      addPokemon(firstEmptyCell.x, firstEmptyCell.y, entity)
    }
  }

  function updateSelectedPokemon(pkm: PkmWithCustom) {
    setSelectionValue(pkm)
    if (selectedPokemon != null) {
      selectedPokemon.emotion = pkm.emotion
      selectedPokemon.shiny = pkm.shiny
      updateBoard([...board])
    }
  }

  function changeAvatar() {
    if (
      selection &&
      props.onChangeAvatar &&
      Object.values(Pkm).includes((selection as PkmWithCustom).name)
    ) {
      props.onChangeAvatar(selection as PkmWithCustom)
    }
  }

  function snapshot() {
    try {
      if (!spectatedPlayer) return
      updateBoard(
        values(spectatedPlayer.board)
          .filter((pokemon) => !isOnBench(pokemon))
          .map((p) => {
            const consumedSetupState = p as typeof p & {
              fireShardCount?: number
              smokedFiletCount?: number
            }
            return {
              name: p.name,
              emotion: p.emotion,
              shiny: p.shiny,
              tm: p.tm !== Ability.DEFAULT ? p.tm : undefined,
              supercharged: p.supercharged || undefined,
              items: values(p.items),
              fireShardCount:
                (consumedSetupState.fireShardCount ?? 0) > 0
                  ? consumedSetupState.fireShardCount
                  : undefined,
              smokedFiletCount:
                (consumedSetupState.smokedFiletCount ?? 0) > 0
                  ? consumedSetupState.smokedFiletCount
                  : undefined,
              x: p.positionX,
              y: p.positionY
            }
          })
      )
    } catch (e) {
      console.error("Failed to snapshot board:", e)
    }
  }

  function reset() {
    updateBoard([])
    props.onResetBoard?.()
  }

  function saveFile() {
    // save board to local JSON file
    const blob = new Blob([JSON.stringify(board)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = props.fileName ?? "board.json"
    a.click()
    URL.revokeObjectURL(url)
  }

  function loadFile() {
    // load from local JSON file
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "application/json"
    input.addEventListener("change", async (e) => {
      if (!input.files) return
      const file = input.files![0]
      const reader = new FileReader()
      reader.onload = async (e) => {
        if (!e.target) return
        try {
          const data = JSON.parse(e.target.result as string)
          if (!data || !Array.isArray(data)) {
            throw new Error("Invalid file content")
          } else {
            updateBoard(data)
          }
        } catch (e) {
          alert("Invalid file")
        }
      }
      reader.readAsText(file)
    })
    input.click()
  }

  function overwriteBoard() {
    rooms.game?.send(Transfer.OVERWRITE_BOARD, board)
  }

  return (
    <div
      id="team-builder"
      className={[
        props.showSelectedEntity === false ? "without-selection-panel" : "",
        props.showActions === false ? "without-actions" : "",
        props.showItemPicker === false ? "without-item-picker" : "",
        props.showSynergies === false ? "without-synergies" : ""
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {props.showSynergies !== false && (
        <div className="synergies-container my-box">
          <Synergies synergies={synergies} tooltipPortal={false} />
        </div>
      )}
      {props.showActions !== false && (
        <div className="actions">
          {ingame && isAdmin && (
            <details>
              <summary>Admin</summary>
              <button
                className="bubbly blue"
                onClick={overwriteBoard}
                disabled={props.disabled}
              >
                Overwrite game board
              </button>
            </details>
          )}
          {ingame && (
            <button
              className="bubbly blue"
              onClick={snapshot}
              disabled={props.disabled}
            >
              <img src="assets/ui/photo.svg" /> {t("snapshot")}
            </button>
          )}
          {!inBotBuilder && (
            <button
              className="bubbly dark"
              onClick={saveFile}
              disabled={props.disabled}
            >
              <img src="assets/ui/save.svg" /> {t("save")}
            </button>
          )}
          {!inBotBuilder && (
            <button
              className="bubbly dark"
              onClick={loadFile}
              disabled={props.disabled}
            >
              <img src="assets/ui/load.svg" /> {t("load")}
            </button>
          )}
          <button
            className="bubbly red"
            onClick={reset}
            disabled={props.disabled}
          >
            <img src="assets/ui/trash.svg" /> {t("reset")}
          </button>
        </div>
      )}
      <TeamEditor
        board={board}
        highlightedCells={props.highlightedCells}
        handleEditorClick={handleEditorClick}
        handleDrop={handleDrop}
        showBench={inBotBuilder}
      />
      {props.showSelectedEntity !== false && (
        <SelectedEntity entity={selection} onChange={updateSelectedPokemon} />
      )}
      {props.showItemPicker !== false && (
        <ItemPicker
          selectEntity={(nextSelection) => setSelectionValue(nextSelection)}
          selected={selection}
        />
      )}
      <PokemonPicker
        selectEntity={(e) => setSelectionValue(e as PkmWithCustom | Item)}
        addEntity={addPokemonOnFirstEmptyCell}
        selected={selection}
      />
      {props.bot && props.onChangeAvatar && (
        <BotAvatar
          bot={props.bot}
          onChangeAvatar={props.onChangeAvatar}
          onClick={changeAvatar}
        />
      )}
      {props.error && <p className="error">{props.error}</p>}
      {props.disabled && (
        <div className="team-builder-lock">
          <span>Simulation running...</span>
        </div>
      )}
    </div>
  )
}

function buildDetailedPokemonForBuilder(detail: IDetailledPokemon) {
  const pokemon = PokemonFactory.createPokemonFromName(detail.name, {
    emotion: detail.emotion,
    shiny: detail.shiny
  })
  detail.items.forEach((item) => pokemon.items.add(item))
  if (detail.tm && detail.tm !== Ability.DEFAULT) {
    pokemon.tm = detail.tm
    pokemon.skill = detail.tm
    pokemon.maxPP = 100
    pokemon.pp = 100
  }
  if ((detail.fireShardCount ?? 0) > 0) {
    pokemon.addAttack(3 * (detail.fireShardCount ?? 0))
    pokemon.addSpeed(3 * (detail.fireShardCount ?? 0))
  }
  applySmokedFiletStacks(pokemon, Math.min(detail.smokedFiletCount ?? 0, 10))
  if (detail.supercharged) {
    pokemon.supercharged = true
  }
  return pokemon
}

function getRareCandyEvolutionTarget(
  pokemon: ReturnType<typeof PokemonFactory.createPokemonFromName>
) {
  if (pokemon.evolution && pokemon.evolution !== Pkm.DEFAULT) {
    return pokemon.evolution
  }

  return pokemon.evolutions.find((evolution) => evolution !== Pkm.DEFAULT)
}
function applySmokedFiletStacks(
  pokemon: ReturnType<typeof PokemonFactory.createPokemonFromName>,
  count: number
) {
  if (count <= 0) {
    return
  }

  pokemon.addMaxHP(-5 * count)
  pokemon.addAttack(3 * count)
  pokemon.addAbilityPower(10 * count)
}
